import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';
import type { IGggFinanceProjectRow } from '../types';
import type { IFinanceProjectOrder } from '../utils/projectOrder';

const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

export const getFinanceAutoSaveMonthKey = (selectedMonth: any): string => {
  if (!selectedMonth) {
    return '';
  }
  return moment(selectedMonth).format('YYYY-MM');
};

export const getFinanceAutoSavePageKey = (selectedMonth: any): string => {
  const monthKey = getFinanceAutoSaveMonthKey(selectedMonth);
  if (!monthKey) {
    return EDIT_PAGE_KEY_MAP.studio_live_finance;
  }
  return `${EDIT_PAGE_KEY_MAP.studio_live_finance}_${monthKey}`;
};

interface IParams {
  isEditing: boolean;
  selectedMonth: any;
  projects: IGggFinanceProjectRow[];
  projectOrderList: IFinanceProjectOrder[];
  setProjects: (projects: IGggFinanceProjectRow[]) => void;
  setProjectOrderList: (orders: IFinanceProjectOrder[]) => void;
}

interface IAutoSaveData {
  datatime: string;
  projects: IGggFinanceProjectRow[];
  projectOrderList: IFinanceProjectOrder[];
  baselineProjects: IGggFinanceProjectRow[];
  baselineProjectOrderList: IFinanceProjectOrder[];
}

const EMPTY_DATA: IAutoSaveData = {
  datatime: '',
  projects: [],
  projectOrderList: [],
  baselineProjects: [],
  baselineProjectOrderList: [],
};

export const useLiveFinanceAutoSave = ({
  isEditing,
  selectedMonth,
  projects,
  projectOrderList,
  setProjects,
  setProjectOrderList,
}: IParams) => {
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [baselineProjects, setBaselineProjects] = useState<IGggFinanceProjectRow[]>([]);
  const [baselineProjectOrderList, setBaselineProjectOrderList] = useState<
    IFinanceProjectOrder[]
  >([]);

  const monthKey = useMemo(
    () => getFinanceAutoSaveMonthKey(selectedMonth),
    [selectedMonth],
  );
  const autoSavePageKey = useMemo(
    () => getFinanceAutoSavePageKey(selectedMonth),
    [selectedMonth],
  );
  const initialParamsRef = useRef<IAutoSaveData>(deepClone(EMPTY_DATA));

  const autoSaveMethods: any = useAutoSaveParams({
    pageKey: autoSavePageKey,
  });

  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } =
    autoSaveMethods;

  const captureBaseline = useCallback(
    (projectList: IGggFinanceProjectRow[], orderList: IFinanceProjectOrder[]) => {
      const clonedProjects = deepClone(projectList || []);
      const clonedOrders = deepClone(orderList || []);

      setBaselineProjects(clonedProjects);
      setBaselineProjectOrderList(clonedOrders);

      initialParamsRef.current = {
        datatime: monthKey,
        projects: clonedProjects,
        projectOrderList: clonedOrders,
        baselineProjects: clonedProjects,
        baselineProjectOrderList: clonedOrders,
      };
    },
    [monthKey],
  );

  const clearBaseline = useCallback(() => {
    setBaselineProjects([]);
    setBaselineProjectOrderList([]);
    initialParamsRef.current = deepClone(EMPTY_DATA);
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing || !monthKey) return false;
    const hasProjectChanges =
      JSON.stringify(projects || []) !== JSON.stringify(baselineProjects || []);
    const hasOrderChanges =
      JSON.stringify(projectOrderList || []) !==
      JSON.stringify(baselineProjectOrderList || []);
    return hasProjectChanges || hasOrderChanges;
  }, [
    isEditing,
    monthKey,
    projects,
    projectOrderList,
    baselineProjects,
    baselineProjectOrderList,
  ]);

  const currentSaveParams = useMemo(() => {
    if (!isEditing || !monthKey) {
      return deepClone(EMPTY_DATA);
    }
    return {
      datatime: monthKey,
      projects: projects || [],
      projectOrderList: projectOrderList || [],
      baselineProjects: baselineProjects || [],
      baselineProjectOrderList: baselineProjectOrderList || [],
    };
  }, [
    isEditing,
    monthKey,
    projects,
    projectOrderList,
    baselineProjects,
    baselineProjectOrderList,
  ]);

  useAutoSaveParams({
    pageKey: autoSavePageKey,
    initialParams: initialParamsRef.current,
    saveParamsCur: currentSaveParams,
    isBegin: isEditing && !!monthKey,
    isChangeValue: true,
  });

  useEffect(() => {
    if (!isEditing) {
      initialParamsRef.current = deepClone(EMPTY_DATA);
    }
  }, [isEditing]);

  const handleEditWithAutoSave = useCallback(
    (onNoRestore: () => void) => {
      if (monthKey && showRestorePrompt(autoSavePageKey)) {
        setAutoSaveVisible(true);
        return;
      }
      onNoRestore();
    },
    [monthKey, showRestorePrompt, autoSavePageKey],
  );

  const handleAutoSaveConfirm = useCallback((): IAutoSaveData | null => {
    const savedData = (getSaveParamsValue(autoSavePageKey) || {}) as IAutoSaveData;
    setAutoSaveVisible(false);

    // Ignore stale autosave from a different month (page key should already isolate this).
    if (savedData.datatime && monthKey && savedData.datatime !== monthKey) {
      deleteSaveParamsByPageKey(autoSavePageKey);
      return null;
    }

    if (Array.isArray(savedData.projects)) {
      setProjects(savedData.projects);
    }
    if (Array.isArray(savedData.projectOrderList)) {
      setProjectOrderList(savedData.projectOrderList);
    }

    const restoredBaselineProjects =
      savedData.baselineProjects || savedData.projects || [];
    const restoredBaselineOrders =
      savedData.baselineProjectOrderList || savedData.projectOrderList || [];

    setBaselineProjects(restoredBaselineProjects);
    setBaselineProjectOrderList(restoredBaselineOrders);

    initialParamsRef.current = {
      datatime: savedData.datatime || monthKey,
      projects: savedData.projects || [],
      projectOrderList: savedData.projectOrderList || [],
      baselineProjects: restoredBaselineProjects,
      baselineProjectOrderList: restoredBaselineOrders,
    };

    deleteSaveParamsByPageKey(autoSavePageKey);
    return savedData;
  }, [
    getSaveParamsValue,
    autoSavePageKey,
    monthKey,
    setProjects,
    setProjectOrderList,
    deleteSaveParamsByPageKey,
  ]);

  const handleAutoSaveCancel = useCallback(() => {
    setAutoSaveVisible(false);
    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [deleteSaveParamsByPageKey, autoSavePageKey]);

  const clearAutoSave = useCallback(() => {
    clearBaseline();
    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [clearBaseline, deleteSaveParamsByPageKey, autoSavePageKey]);

  return {
    autoSaveVisible,
    hasUnsavedChanges,
    captureBaseline,
    clearBaseline,
    handleEditWithAutoSave,
    handleAutoSaveConfirm,
    handleAutoSaveCancel,
    clearAutoSave,
  };
};
